import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import RemoveIcon from '@material-ui/icons/Remove'
import React, { useEffect, useState } from 'react'

import {
  createPlannerQuota,
  fetchPlannerGradeGroup,
  fetchPlannerGradeSubject,
  fetchPlannerQuota,
  fetchPlannerSubject,
  updatePlannerQuota
} from 'api'
import { showToast } from 'app/helpers'

const items = (d) =>
  d && Array.isArray(d.results) ? d.results : Array.isArray(d) ? d : []

/** Weekly quotas per grade group — stepper per subject (mirrors the prototype). */
const Quotas = () => {
  const [grades, setGrades] = useState([])
  const [gradeIdx, setGradeIdx] = useState(0)
  const [subjectLabels, setSubjectLabels] = useState({})
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const load = async () => {
      try {
        const [gg, subs] = await Promise.all([
          fetchPlannerGradeGroup({}),
          fetchPlannerSubject({})
        ])
        setGrades(items(gg))
        const map = {}
        items(subs).forEach((s) => {
          map[s.uuid] = s.label
        })
        setSubjectLabels(map)
      } catch (e) {
        showToast('Failed to load grades / subjects', 'danger')
      }
    }
    load()
  }, [])

  const grade = grades[gradeIdx]

  useEffect(() => {
    if (!grade) return
    const load = async () => {
      setLoading(true)
      try {
        const [gs, qs] = await Promise.all([
          fetchPlannerGradeSubject({ queries: { grade_group: grade.uuid } }),
          fetchPlannerQuota({ queries: { grade_group: grade.uuid } })
        ])
        const quotaBySubject = {}
        items(qs).forEach((q) => {
          quotaBySubject[q.subject] = q
        })
        setRows(
          items(gs).map((link) => {
            const q = quotaBySubject[link.subject]
            return {
              subject: link.subject,
              label: subjectLabels[link.subject] || link.subject_label || '—',
              count: q ? q.weekly_required_count : 0,
              quotaUuid: q ? q.uuid : null
            }
          })
        )
      } catch (e) {
        showToast('Failed to load quotas', 'danger')
      }
      setLoading(false)
    }
    load()
  }, [grade && grade.uuid, subjectLabels])

  const bump = (i, delta) =>
    setRows((rs) =>
      rs.map((r, idx) =>
        idx === i ? { ...r, count: Math.max(0, r.count + delta) } : r
      )
    )

  const save = async () => {
    if (!grade) return
    setSaving(true)
    try {
      await Promise.all(
        rows.map((r) =>
          r.quotaUuid
            ? updatePlannerQuota({
              uuid: r.quotaUuid,
              grade_group: grade.uuid,
              subject: r.subject,
              weekly_required_count: r.count
            })
            : createPlannerQuota({
              grade_group: grade.uuid,
              subject: r.subject,
              weekly_required_count: r.count
            })
        )
      )
      showToast('Quotas saved', 'success')
    } catch (e) {
      showToast('Failed to save quotas', 'danger')
    }
    setSaving(false)
  }

  const total = rows.reduce((s, r) => s + r.count, 0)

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        Weekly Quotas per Grade Group
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Set how many sessions of each type teachers must plan per week.
      </Typography>

      <Tabs
        value={gradeIdx}
        onChange={(e, v) => setGradeIdx(v)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
      >
        {grades.map((g) => (
          <Tab key={g.uuid} label={`${g.emoji || ''} ${g.name}`} />
        ))}
      </Tabs>

      {loading ? (
        <Box mt={3}>
          <CircularProgress />
        </Box>
      ) : (
        <Box mt={3} display="flex" flexWrap="wrap" style={{ gap: 12 }}>
          {rows.map((r, i) => (
            <Paper key={r.subject} variant="outlined" style={{ padding: 12, minWidth: 180 }}>
              <Typography variant="subtitle2">{r.label}</Typography>
              <Box display="flex" alignItems="center" mt={1}>
                <IconButton size="small" onClick={() => bump(i, -1)}>
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography variant="h6" style={{ margin: '0 12px' }}>
                  {r.count}
                </Typography>
                <IconButton size="small" onClick={() => bump(i, 1)}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          ))}
          {!rows.length && (
            <Typography variant="body2" color="textSecondary">
              No subjects assigned to this grade yet (set them on the Subjects tab).
            </Typography>
          )}
        </Box>
      )}

      <Box mt={3} display="flex" alignItems="center" style={{ gap: 16 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={save}
          disabled={saving || !rows.length}
        >
          {saving ? 'Saving…' : 'Save Quotas'}
        </Button>
        <Typography variant="body2" color="textSecondary">
          Weekly total: {total} required sessions
        </Typography>
      </Box>
    </Box>
  )
}

export default Quotas
